import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

// MUI core components
import {
  TextField,
  Grid,
  Alert,
  Button,
  Card,
  CardMedia,
  Container,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
  Badge,
  Divider,
  useMediaQuery,
  Skeleton,
} from "@mui/material";

// MUI styles and icons
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { Fade, Slide, Grow } from "@mui/material";
import AccountIcon from "@mui/icons-material/AccountBox";
import VerifiedIcon from "@mui/icons-material/Verified";
import ContractIcon from "@mui/icons-material/Download";
import DownloadIcon from "@mui/icons-material/Download";

import LocationIcon from "@mui/icons-material/LocationOn";
import AgreementIcon from "@mui/icons-material/Handshake";
import CarIcon from "@mui/icons-material/DirectionsCar";
import OwnerIcon from "@mui/icons-material/Face6";
import RulerIcon from "@mui/icons-material/Straighten";
import TreeIcon from "@mui/icons-material/Park";

import { Helmet } from "react-helmet-async";

// API calls
import { getAuctionById } from "../../api/auctions";
import { getVerificationStatus } from "../../api/users";
import { createBid, getBids } from "../../api/bids";

// Custom components
import CountdownTimer from "../../components/UI/CountdownTimer";
import MUIAlert from "../../components/UI/MUIAlert";
import MUIConfirm from "../../components/UI/MUIConfirm";
import MapPreview from "../../components/UI/MapPreview";

// Other libraries
import { useTranslation } from "react-i18next";
import { io } from "socket.io-client";

let accessAgreementLabels;
let fellingTypeLabels;
let accessConditionsLabels;
let measurementStatusLabels;
let fellingOwnerLabels;
let forestStatusLabels;
let treeTypeLabels;

const ViewAuction = () => {
  // Hooks from libraries
  const { t } = useTranslation();
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();

  accessAgreementLabels = {
    none: t("forms.access_agreement_none"),
    agreed: t("forms.access_agreement_agreed"),
    verbal: t("forms.access_agreement_verbal"),
    not_agreed: t("forms.access_agreement_not_agreed"),
  };

  accessConditionsLabels = {
    none: t("forms.access_conditions_none"),
    bad: t("forms.access_conditions_bad"),
    okay: t("forms.access_conditions_okay"),
    good: t("forms.access_conditions_good"),
  };

  fellingTypeLabels = {
    clear: t("forms.felling_type_clear"),
    selection: t("forms.felling_type_selection"),
    maintenance: t("forms.felling_type_maintenance"),
    random: t("forms.felling_type_random"),
  };

  forestStatusLabels = {
    none: t("forms.forest_status_none"),
    unknown: t("forms.forest_status_unknown"),
    partially: t("forms.forest_status_partially"),
    fully: t("forms.forest_status_fully"),
  };

  measurementStatusLabels = {
    no: t("forms.measurement_status_no"),
    yes: t("forms.measurement_status_yes"),
  };

  fellingOwnerLabels = {
    same: t("forms.felling_owner_same"),
    different: t("forms.felling_owner_different"),
  };

  treeTypeLabels = {
    unknown: t("forms.tree_type_unknown"),
    priede: t("forms.tree_type_priede"),
    egle: t("forms.tree_type_egle"),
    berzs: t("forms.tree_type_berzs"),
    apse: t("forms.tree_type_apse"),
    malksnis: t("forms.tree_type_malksnis"),
    balksnis: t("forms.tree_type_balksnis"),
    ozols: t("forms.tree_type_ozols"),
    osis: t("forms.tree_type_osis"),
    liepa: t("forms.tree_type_liepa"),
    bligzna: t("forms.tree_type_bligzna"),
    klava: t("forms.tree_type_klava"),
    sauskegle: t("forms.tree_type_sauskegle"),
    sauskpriede: t("forms.tree_type_sauskpriede"),
    other: t("forms.tree_type_other"),
  };

  const statusLabels = {
    active: t("general.active"),
    inactive: t("general.draft"),
    pending: t("general.pending"),
    completed: t("general.finished"),
  };
  // Socket connection state
  const [socket, setSocket] = useState(null);

  // Auction-related states
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // User-related states
  const [userVerified, setUserVerified] = useState(false);
  const [userTyping, setUserTyping] = useState(false);

  // Bid-related states
  const [highestBid, setHighestBid] = useState(null); // Full price
  const [bidAmount, setBidAmount] = useState(100); // Full price
  const [bidHistory, setBidHistory] = useState(null);
  const [submittingBid, setSubmittingBid] = useState(false);
  const [disabledBid, setDisabledBid] = useState(false);
  const [bidWaitTimer, setBidWaitTimer] = useState(0);
  const [lastAddedBidId, setLastAddedBidId] = useState(null);

  // Confirmation modal states
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [confirmParameter, setConfirmParameter] = useState(null);

  // Alert modal states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success"); // Default severity

  const [trees, setTrees] = useState(null);
  const [userAttachments, setUserAttachments] = useState(null);
  const [userImages, setUserImages] = useState(null);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  const showConfirm = (title, content, onConfirm, parameter) => {
    setConfirmTitle(title);
    setConfirmContent(content);
    setConfirmCallback(() => onConfirm);
    setConfirmParameter(parameter);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmTitle("");
    setConfirmContent("");
    setConfirmCallback(null);
    setConfirmParameter(null);
  };

  const handleConfirmAction = () => {
    if (confirmCallback && confirmParameter) {
      confirmCallback(confirmParameter);
    }
    handleConfirmClose();
  };

  const handleBidChange = (e) => {
    setUserTyping(true);
    const value = Number(e.target.value);
    setBidAmount(value);
  };

  const handleIncrement = (amount) => {
    setBidAmount((bidAmount) => bidAmount + amount);
  };

  const handleBidSubmit = async (e) => {
    e.preventDefault();

    if (!userVerified) {
      alert(t("errors.user_not_verified_yet"));
      navigate("/sveicinats");
      return;
    }

    if (bidAmount <= highestBid) {
      showAlert(t("view_auction_page.bid_create_failed"), "error");
      return;
    }

    showConfirm(
      t("view_auction_page.confirm_bid_title"),
      t("view_auction_page.confirm_bid"),
      async (params) => {
        setSubmittingBid(true);
        try {
          const bid = await createBid(params.auctionId, params.bidAmount);
          if (bid) {
            showAlert(t("view_auction_page.bid_submitted"));
          }
          setSubmittingBid(false);
          setDisabledBid(true);

          for (let i = 5; i >= 1; i--) {
            setBidWaitTimer(i); // Update the timer display
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
          setDisabledBid(false);
        } catch (error) {
          showAlert(t("view_auction_page.bid_create_failed"), "error");
          setError(error.message || t("view_auction_page.bid_create_failed"));
          setSubmittingBid(false);
        }
      },
      { auctionId: id, bidAmount: bidAmount } // Pass the auctionId to the showConfirm function
    );
  };

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL, {
      transports: ["websocket"], // or ['polling', 'websocket'] if fallback needed
      withCredentials: true,
    });
    setSocket(socket);

    const refreshData = () => {
      fetchAuction();
      fetchBids();
    };

    socket.on("connect", () => {});

    socket.on("new_bid", () => {
      showAlert(t("view_auction_page.price_increase"), "warning");
      refreshData();
    });
    socket.on("auction_published", refreshData);
    socket.on("auction_ended", refreshData);
    socket.on("auction_updated", refreshData);

    const fetchAuction = async () => {
      try {
        const foundAuction = await getAuctionById(id);

        // console.log(foundAuction);
        if (foundAuction._error) {
          navigate("/izsoles");
        }

        if (!foundAuction) {
          navigate("/izsoles");
          setError(t("view_auction_page.auction_not_found"));
        } else {
          setAuction(foundAuction);

          // Calc the top 3 trees and the others
          if (foundAuction.property_type == "felling") {
            const sorted = [...JSON.parse(foundAuction.trees_on_property)].sort(
              (a, b) => b.percentage - a.percentage
            );
            const top3 = sorted.slice(0, 3);
            const rest = sorted.slice(3);
            const restSum = rest.reduce((acc, t) => acc + t.percentage, 0);
            const displayedTrees =
              rest.length > 0
                ? [...top3, { code: "other", percentage: restSum }]
                : top3;
            setTrees(displayedTrees);
          }
          if (JSON.parse(foundAuction.attachments).length > 0) {
            const images = [];
            const attachments = [];
            const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];

            JSON.parse(foundAuction.attachments).forEach((filePath) => {
              const fileName = filePath.split("/").pop(); // extract filename
              const ext = fileName.split(".").pop().toLowerCase();

              if (imageExtensions.includes(ext)) {
                images.push({ path: filePath, name: fileName });
              } else {
                attachments.push({ path: filePath, name: fileName });
              }
            });
            setUserAttachments(attachments);
            setUserImages(images);
          }
          fetchBids(foundAuction);
        }
      } catch (err) {
        navigate("/izsoles");
        setError(t("view_auction_page.fetch_auction_failed"));
      } finally {
        setLoading(false);
      }
    };
    const fetchBids = async (foundAuction) => {
      try {
        const fetchedBidHistory = await getBids();

        if (fetchedBidHistory) {
          const filteredBids = fetchedBidHistory
            .filter((bid) => bid.auction_id == id)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

          const lastBid = filteredBids.length > 0 ? filteredBids[0] : 0;
          setHighestBid(
            lastBid
              ? Number(lastBid?.amount)
              : Number(foundAuction?.starting_price)
          );

          if (!userTyping) {
            setBidAmount(
              lastBid
                ? lastBid.amount + 50
                : Number(foundAuction?.starting_price) + 50
            );
          }

          setBidHistory((prev) => {
            const prevIds = new Set(prev?.map((b) => b.id));

            const incomingIds = new Set(filteredBids.map((b) => b.id));

            const newBids = filteredBids.filter((b) => !prevIds.has(b.id));

            if (newBids.length > 0) {
              setLastAddedBidId(newBids[0].id); // assuming newest first
            }

            return [
              ...newBids,
              ...filteredBids.filter((b) => prevIds.has(b.id)),
            ];
          });
        }
      } catch (err) {
        setError(t("view_auction_page.fetch_bids_failed"));
      }
    };
    const fetchUserStatus = async () => {
      try {
        const userStatus = await getVerificationStatus();
        // setUserVerified(true);
        setUserVerified(userStatus.verified == true);
      } catch (err) {
        setError(t("view_auction_page.fetch_auction_failed"));
      }
    };

    socket.on("auction_updated", (data) => {
      fetchAuction();
      fetchBids();
    });

    fetchUserStatus();
    fetchAuction();

    return () => {
      socket.disconnect();
    };
  }, [id]);

  if (error) {
    return (
      <Container>
        <Box sx={{ padding: 3 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t("seo.title.view_auction")}</title>
        <meta property="og:title" content={t("seo.title.view_auction")} />

        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box
        sx={{
          backgroundColor: "white",
        }}
      >
        {loading ? (
          <Box
            sx={{
              height: "400px",
              width: "100%",
              display: "block",
            }}
          ></Box>
        ) : (
          <Box
            sx={{
              height: "55vh",
              width: "100%",
              display: "block",
            }}
          >
            <MapPreview
              cadastreId={auction?.cadastre}
              full
              lat={
                JSON.parse(auction?.coordinates).lat
                  ? JSON.parse(auction?.coordinates).lat
                  : 3
              }
              lng={
                JSON.parse(auction?.coordinates).lng
                  ? JSON.parse(auction?.coordinates).lng
                  : 3
              }
            />
          </Box>
        )}
        <Box
          sx={{
            mt: { xs: 0, md: 4 },
            mb: { xs: 0, md: 4 },
            mx: "auto",
            maxWidth: { xs: "100vw", md: "87vw" },
          }}
        >
          <MUIConfirm
            open={confirmOpen}
            onClose={handleConfirmClose}
            title={confirmTitle}
            content={confirmContent}
            onConfirm={handleConfirmAction}
          />

          <MUIAlert
            open={alertOpen}
            onClose={handleAlertClose}
            severity={alertSeverity}
            message={alertMessage}
          />

          <Box sx={{ padding: { xs: 3, md: 4 } }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: "50px",
                color: "gray",
                borderColor: "gray",
                textTransform: "none",
                mb: 3,
              }}
            >
              {t("navigation.go_back")}
            </Button>

            <Grid container spacing={3} alignItems="stretch">
              {/* LEFT SIDE */}
              <Grid item xs={12} md={7}>
                {auction?.status == "active" ? ( // Use optional chaining here
                  <>
                    {loading ? (
                      <Skeleton variant="text" width={300} height={40} />
                    ) : (
                      <>
                        <Typography
                          color="primary"
                          variant="h2"
                          sx={{
                            fontSize: "3em",
                          }}
                        >
                          {auction?.property_name}
                          <Chip
                            sx={{
                              ml: 1,
                              px: 1,
                              backgroundColor:
                                theme.palette.success.background2,
                              borderStyle: "solid",
                              borderWidth: "1px",
                              borderColor: theme.palette.success.main,
                              color: theme.palette.success.main,
                            }}
                            label={statusLabels[auction.status]}
                            color="success"
                            size="small"
                            variant="contained"
                          />
                        </Typography>
                        <Box>
                          <Typography
                            variant="h2"
                            color="secondary"
                            sx={{
                              fontSize: "2em",
                              display: "inline-block",
                              fontWeight: "bold",
                            }}
                          >
                            €
                            {Number(
                              highestBid || auction.starting_price
                            ).toLocaleString()}
                          </Typography>
                          <Typography
                            variant="h2"
                            color="primary"
                            sx={{
                              fontSize: "2em",
                              display: "inline-block",
                            }}
                          >
                            <span style={{ padding: "0px 5px" }}>•</span>
                            {Number(
                              (auction.property_size &&
                                Number(auction.property_size) > 0) ||
                                auction.harvestable_area
                            ).toLocaleString()}{" "}
                            ha
                          </Typography>
                        </Box>
                        {/* <Typography color='primary' variant="subtitle">
                                        {auction.property_address}
                                    </Typography> */}
                      </>
                    )}
                    {loading ? (
                      <Skeleton variant="text" width={200} height={30} />
                    ) : (
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        <CountdownTimer endTime={auction.end_time} />
                      </Typography>
                    )}
                  </>
                ) : null}

                <Grid container spacing={3} alignItems="stretch">
                  <Grid item xs={12} md={11}>
                    <Typography
                      color="primary"
                      sx={{
                        display: "block",
                        mt: { xs: 1, sm: 5 },
                        mb: 1,
                        fontSize: "1.8em",
                        fontWeight: "900",
                      }}
                      variant="body"
                    >
                      {auction?.property_type == "property" &&
                        t("view_auction_page.about_property")}
                      {auction?.property_type == "felling" &&
                        t("view_auction_page.about_felling")}
                      {(auction?.property_type == "timber" ||
                        auction?.property_type == "wood_chips") &&
                        t("view_auction_page.about_auction")}
                    </Typography>
                    {/* 3 HIGHLIGHTS */}
                    <Grid container spacing={1} alignItems="stretch">
                      <Grid item xs={4} md={4}>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.primary.superlight,
                            borderStyle: "solid",
                            borderWidth: "1px",
                            borderColor: theme.palette.primary.superlight2,
                            borderRadius: "999px",
                            py: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "Center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "1.1em", sm: "1.7em" },
                              lineHeight: "1em",
                            }}
                            fontWeight={"900"}
                            color="secondary"
                            variant="body"
                          >
                            {auction?.property_type == "felling" &&
                              auction.harvestable_area + "ha"}
                            {auction?.property_type == "property" &&
                              auction.property_size + "ha"}
                          </Typography>
                          <Typography
                            sx={{
                              mt: { xs: 0.5, sm: 0 },
                              textAlign: "center",
                              fontSize: { xs: "0.7em", sm: "0.8em" },
                              maxWidth: { xs: "50%", sm: "100%" },
                              lineHeight: { xs: "1em", sm: "1.5em" },
                            }}
                            fontWeight={"900"}
                            variant="subtitle"
                          >
                            {auction?.property_type == "felling" &&
                              t("forms.harvestable_area")}
                            {auction?.property_type == "property" &&
                              t("forms.property_size")}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} md={4}>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.primary.superlight,
                            borderStyle: "solid",
                            borderWidth: "1px",
                            borderColor: theme.palette.primary.superlight2,
                            borderRadius: "999px",
                            py: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "Center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "1.1em", sm: "1.7em" },
                              lineHeight: "1em",
                            }}
                            fontWeight={"900"}
                            color="secondary"
                            variant="body"
                          >
                            {auction?.property_type == "felling" &&
                              auction.liquid_felling_area + "ha"}
                            {auction?.property_type == "property" &&
                              (auction.forest_status != "none"
                                ? auction.forest_size + "ha"
                                : "0ha")}
                          </Typography>
                          <Typography
                            sx={{
                              mt: { xs: 0.5, sm: 0 },
                              textAlign: "center",
                              fontSize: { xs: "0.7em", sm: "0.8em" },
                              maxWidth: { xs: "50%", sm: "100%" },
                              lineHeight: { xs: "1em", sm: "1.5em" },
                            }}
                            fontWeight={"900"}
                            variant="subtitle"
                          >
                            {auction?.property_type == "felling" &&
                              t("forms.liquid_felling_area")}
                            {auction?.property_type == "property" &&
                              t("forms.forest_size")}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} md={4}>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.primary.superlight,
                            borderStyle: "solid",
                            borderWidth: "1px",
                            borderColor: theme.palette.primary.superlight2,
                            borderRadius: "999px",
                            py: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "Center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: "1.1em", sm: "1.7em" },
                              lineHeight: "1em",
                            }}
                            fontWeight={"900"}
                            color="secondary"
                            variant="body"
                          >
                            {auction?.property_type == "felling" &&
                              fellingTypeLabels[auction.felling_type]}
                            {auction?.property_type == "property" &&
                              (auction.forest_status != "none"
                                ? forestStatusLabels[auction.forest_status]
                                : t("view_auction_page.no_forest"))}
                          </Typography>
                          <Typography
                            sx={{
                              mt: { xs: 0.5, sm: 0 },
                              textAlign: "center",
                              fontSize: { xs: "0.7em", sm: "0.8em" },
                              maxWidth: { xs: "50%", sm: "100%" },
                              lineHeight: { xs: "1em", sm: "1.5em" },
                            }}
                            fontWeight={"900"}
                            variant="subtitle"
                          >
                            {auction?.property_type == "felling" &&
                              t("forms.felling_type")}
                            {auction?.property_type == "property" &&
                              t("forms.forest_status")}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    {/* TREES */}
                    {auction?.property_type == "felling" && (
                      <Grid
                        sx={{ mt: 3 }}
                        container
                        spacing={1}
                        alignItems="stretch"
                      >
                        {trees.map((tree, index) => (
                          <Grid
                            key={index}
                            item
                            xs={12}
                            md={
                              trees.length === 1
                                ? 12
                                : trees.length === 2
                                ? 6
                                : trees.length === 3
                                ? 4
                                : 3
                            }
                          >
                            <Box
                              sx={{
                                backgroundColor:
                                  theme.palette.success.background2,
                                borderStyle: "solid",
                                borderWidth: "1px",
                                borderColor: theme.palette.success.lighter,
                                borderRadius: "9999px",
                                py: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "Center",
                              }}
                            >
                              {/* <TreeIcon /> */}
                              <Typography
                                sx={{ fontSize: "1.8em", lineHeight: "1em" }}
                                fontWeight={"bold"}
                                color="secondary"
                                variant="body"
                              >
                                {tree.percentage}%
                              </Typography>
                              <Typography
                                sx={{ fontSize: "0.8em", lineHeight: "1em" }}
                                fontWeight={"bold"}
                                color="primary"
                                variant="body"
                              >
                                {treeTypeLabels[tree.code]}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <Grid item sx={12} md={11}>
                  <Typography
                    color="primary"
                    sx={{
                      display: "block",
                      mt: { xs: 1, sm: 5 },
                      mb: 2,
                      fontSize: "1.8em",
                      fontWeight: "900",
                    }}
                    variant="body"
                  >
                    {t("view_auction_page.location")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1em",
                      mt: 0,
                      pt: 0,
                      lineHeight: "1em",
                      display: "flex",
                    }}
                    variant="subtitle2"
                    color="primary"
                  >
                    <LocationIcon
                      sx={{ fontSize: "1em", lineHeight: "0.5em", mr: 1 }}
                    />
                    {auction?.cadastre
                      ? t("view_auction_page.cadastre") +
                        " " +
                        auction?.cadastre +
                        " - " +
                        auction?.property_address
                      : auction?.property_address}
                  </Typography>

                  {auction?.property_type == "felling" && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        sx={{
                          fontSize: "1em",
                          mt: 0,
                          pt: 0,
                          lineHeight: "1em",
                          display: "flex",
                        }}
                        variant="subtitle2"
                        color="primary"
                      >
                        <AgreementIcon
                          sx={{ fontSize: "1em", lineHeight: "0.5em", mr: 1 }}
                        />
                        {/* {t("view_auction_page.access_agreement") + " - "} */}
                        {accessAgreementLabels[auction.access_agreement]}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        sx={{
                          fontSize: "1em",
                          mt: 0,
                          pt: 0,
                          lineHeight: "1em",
                          display: "flex",
                        }}
                        variant="subtitle2"
                        color="primary"
                      >
                        <OwnerIcon
                          sx={{ fontSize: "1em", lineHeight: "0.5em", mr: 1 }}
                        />
                        {/* {t("view_auction_page.owner") + " - "} */}
                        {fellingOwnerLabels[auction.felling_owner]}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        sx={{
                          fontSize: "1em",
                          mt: 0,
                          pt: 0,
                          lineHeight: "1em",
                          display: "flex",
                        }}
                        variant="subtitle2"
                        color="primary"
                      >
                        <CarIcon
                          sx={{ fontSize: "1em", lineHeight: "0.5em", mr: 1 }}
                        />
                        {/* {t("view_auction_page.access_conditions") + " - "} */}
                        {accessConditionsLabels[auction.access_conditions]}
                      </Typography>
                      <Divider sx={{ my: 2 }} />

                      <Typography
                        sx={{
                          fontSize: "1em",
                          mt: 0,
                          pt: 0,
                          lineHeight: "1em",
                          display: "flex",
                        }}
                        variant="subtitle2"
                        color="primary"
                      >
                        <RulerIcon
                          sx={{ fontSize: "1em", lineHeight: "0.5em", mr: 1 }}
                        />
                        {/* {t("view_auction_page.measurements") + " - "} */}
                        {measurementStatusLabels[auction.measurement_status]}
                      </Typography>
                    </>
                  )}
                </Grid>

                {/* Extra info */}
                <Grid item sx={12} md={11}>
                  <Typography
                    sx={{
                      display: "block",
                      mt: 6,
                      mb: 1,
                      fontSize: "1.8em",
                      fontWeight: "900",
                    }}
                    variant="body"
                    color="primary"
                  >
                    {t("view_auction_page.extra_title")}
                  </Typography>
                  <Paper
                    sx={{
                      mb: 3,
                      padding: 0,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "center",
                      // backgroundColor: theme.palette.background.default2,
                      // borderColor: theme.palette.primary.superlight2
                      borderColor: "transparent",
                    }}
                  >
                    {auction?.extra_info && (
                      <Grid container>
                        <Typography
                          sx={{
                            fontSize: "1em",
                            mt: 0,
                            pt: 0,
                            lineHeight: "1em",
                          }}
                          variant="subtitle2"
                          color="primary"
                        >
                          {auction.extra_info}
                        </Typography>
                      </Grid>
                    )}
                  </Paper>
                </Grid>

                {/* CA */}
                <Grid sx={{ mt: 5 }} container spacing={1} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography
                      sx={{
                        display: "block",
                        mt: 0,
                        mb: 1,
                        fontSize: "1.8em",
                        fontWeight: "900",
                      }}
                      variant="body"
                      color="primary"
                    >
                      {t("view_auction_page.contract_title")}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1em",
                        mt: 0,
                        pt: 0,
                        lineHeight: "0.5em",
                      }}
                      variant="subtitle2"
                      color="primary"
                    >
                      {auction?.signature_type == "digital" && (
                        <Typography
                          variant="subtitle"
                          sx={{ display: "inline-block" }}
                          fontWeight={"bold"}
                          color="secondary"
                        >
                          {t("view_auction_page.sign_digitally")}

                          {auction?.contract_type != "standard" &&
                            " (" + t("view_auction_page.view_attachment") + ")"}
                        </Typography>
                      )}
                      {auction?.signature_type == "physical" && (
                        <Typography
                          variant="subtitle"
                          sx={{ display: "inline-block" }}
                          fontWeight={"bold"}
                          color="secondary"
                        >
                          {t("view_auction_page.sign_physically")}
                          {auction?.contract_type != "standard" &&
                            " (" + t("view_auction_page.view_attachment") + ")"}
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  {auction?.contract_type == "standard" && (
                    <Grid item xs={12} md={4}>
                      <a href="/liguma-paraugs.pdf" target="_blank">
                        <Button
                          startIcon={<ContractIcon />}
                          fullWidth={isMobile}
                          sx={{
                            backgroundColor: theme.palette.primary.superlight,
                            borderStyle: "solid",
                            borderWidth: "1px",
                            borderColor: theme.palette.primary.superlight2,
                            borderRadius: "999px",
                            py: 1.5,
                            px: 2,
                            cursor: "pointer",
                            mt: {
                              xs: 2,
                              sm: 0,
                            },
                          }}
                        >
                          <Typography
                            color="primary"
                            sx={{
                              display: "inline",
                              fontSize: "1em",
                              lineHeight: "1.5em",
                            }}
                            fontWeight={"900"}
                            variant="subtitle"
                          >
                            {t("view_auction_page.view_contract")}
                          </Typography>
                        </Button>
                      </a>
                    </Grid>
                  )}
                </Grid>

                {/* Files */}
                <Grid
                  sx={{ mt: 5 }}
                  container
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Grid item xs={12} md={8}>
                    <Typography
                      sx={{ mt: 0, mb: 2, fontSize: "1.8em", fontWeight: 900 }}
                      variant="body"
                      color="primary"
                    >
                      {t("view_auction_page.attachments_title")}
                    </Typography>

                    <Grid sx={{ my: 2 }} item xs={12} md={12}>
                      <Grid container spacing={2}>
                        {userImages?.map((file, index) => (
                          <Grid
                            key={index}
                            item
                            xs={12} // full width on mobile
                            sm={
                              userImages.length === 1
                                ? 12
                                : userImages.length === 2
                                ? 6
                                : 4
                            } // adjusts per number of images
                            md={
                              userImages.length === 1
                                ? 12
                                : userImages.length === 2
                                ? 6
                                : userImages.length === 3
                                ? 4
                                : 3
                            }
                          >
                            <Card
                              sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                height: 200, // fixed card height
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <a
                                href={`${file.path}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <CardMedia
                                  component="img"
                                  image={`${file.path}`}
                                  alt={file.name}
                                  sx={{ width: "100%", height: "auto" }}
                                />
                              </a>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        gap: 1,
                        alignItems: { xs: "center", md: "flex-start" }, // center on mobile, left on desktop
                      }}
                    >
                      {userAttachments?.map((file, index) => (
                        <a href={`${file.path}`} download>
                          <Button
                            startIcon={<DownloadIcon color="secondary" />}
                            variant="outlined"
                            size="large"
                            color="secondary"
                            sx={{
                              width: { xs: "100%", sm: "auto" },
                              justifyContent: {
                                xs: "center",
                                sm: "flex-start",
                              }, // center text on mobile, left on desktop
                              textTransform: "none",
                              px: 2,
                            }}
                          >
                            <Typography
                              color="secondary"
                              sx={{ fontSize: "1em", lineHeight: "1.5em" }}
                              fontWeight={900}
                              variant="subtitle1"
                            >
                              {t("view_auction_page.view_file") +
                                " - " +
                                file.name}
                            </Typography>
                          </Button>
                        </a>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* RIGHT SIDE */}
              <Grid item xs={12} md={5}>
                {auction?.status == "active" ? ( // Use optional chaining here
                  <form onSubmit={handleBidSubmit}>
                    <Box maxWidth="600px" width="100%" mx="auto" px={0}>
                      <Grid container spacing={2} alignItems="center">
                        {isMobile && (
                          <Grid item xs={12}>
                            <Typography
                              color="primary"
                              sx={{
                                mt: { xs: 2, sm: 5 },
                                mb: 2,
                                fontSize: "1.8em",
                                fontWeight: 900,
                              }}
                              variant="body"
                            >
                              {t("view_auction_page.bid_now")}
                            </Typography>
                          </Grid>
                        )}

                        {/* Input and buttons grouped */}
                        <Grid item xs={12}>
                          <Grid container spacing={1} alignItems="center">
                            {/* Input takes ~40% on sm+ */}
                            <Grid item xs={12} sm={5}>
                              {loading ? (
                                <Skeleton
                                  variant="text"
                                  width="100%"
                                  height={56}
                                />
                              ) : (
                                <TextField
                                  type="number"
                                  label={t("view_auction_page.bid_amount")}
                                  value={Number(bidAmount)}
                                  onChange={handleBidChange}
                                  inputProps={{
                                    min: Number(highestBid)
                                      ? Number(highestBid) + 50
                                      : 150,
                                  }}
                                  fullWidth
                                />
                              )}
                            </Grid>

                            {/* Buttons take ~55% on sm+ */}
                            <Grid item xs={12} sm={7}>
                              <Grid
                                container
                                spacing={1}
                                wrap="nowrap"
                                justifyContent="space-between"
                              >
                                {loading ? (
                                  <>
                                    {[1, 2, 3].map((i) => (
                                      <Grid item xs key={i}>
                                        <Skeleton
                                          variant="rectangular"
                                          width="100%"
                                          height={40}
                                        />
                                      </Grid>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    {[50, 100, 500].map((amount) => (
                                      <Grid item xs key={amount}>
                                        <Button
                                          size="large"
                                          sx={{ py: 1.6 }}
                                          variant="outlined"
                                          fullWidth
                                          onClick={() =>
                                            handleIncrement(amount)
                                          }
                                        >
                                          +{amount}
                                        </Button>
                                      </Grid>
                                    ))}
                                  </>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Submit button full width below */}
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            fullWidth
                            size="large"
                            sx={{
                              borderRadius: "50px",
                              py: 1.2,
                              "&:hover": { backgroundColor: "secondary.light" },
                            }}
                            disabled={
                              disabledBid ||
                              loading ||
                              (Number(highestBid) > 0
                                ? Number(bidAmount) < Number(highestBid) + 50
                                : Number(bidAmount) < 150)
                            }
                          >
                            {submittingBid && (
                              <CircularProgress size={24} color="inherit" />
                            )}
                            {disabledBid
                              ? `You can bid in ${bidWaitTimer} seconds`
                              : t("view_auction_page.place_bid")}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </form>
                ) : null}
                {/* HISTORY */}
                <Grid item xs={12} md={12}>
                  <Typography
                    color="primary"
                    sx={{
                      display: "block",
                      mt: { xs: 2, sm: 5 },
                      mb: 1,
                      fontSize: "1.8em",
                      fontWeight: "900",
                    }}
                    variant="body"
                  >
                    {t("view_auction_page.bid_history")}
                  </Typography>
                  <Paper
                    sx={{
                      mb: { xs: 1, sm: 3 },
                      padding: 3,
                      height: "70%",
                      maxHeight: { xs: "100vw", sm: "25vw" },
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      backgroundColor: theme.palette.primary.superlight,
                      // borderColor: theme.palette.primary.superlight2
                      borderColor: "transparent",
                    }}
                  >
                    <Box>
                      {loading ? (
                        <>
                          <Skeleton variant="text" width="90%" height={20} />
                          <Skeleton variant="text" width="80%" height={20} />
                          <Skeleton variant="text" width="70%" height={20} />
                        </>
                      ) : bidHistory?.length > 0 ? (
                        bidHistory?.map((bid, index) => {
                          const isNew = bid.id === lastAddedBidId;
                          const highestBidAmount = Math.max(
                            ...bidHistory.map((bid) => bid.amount)
                          );
                          return isNew ? (
                            <Box sx={{ overflow: "hidden" }}>
                              <Slide
                                key={bid.id}
                                direction="down"
                                in={isNew}
                                mountOnEnter
                                timeout={300}
                                appear
                                easing="ease-out"
                              >
                                <Fade in={isNew} timeout={400}>
                                  <Typography variant="body2">
                                    {bid?.amount === highestBidAmount
                                      ? "👑 "
                                      : "👤 "}
                                    €{Number(bid.amount).toLocaleString()}
                                    {" — "}
                                    {bid?.business_name
                                      ? bid.business_name + " — "
                                      : bid?.first_name + " — "}
                                    {new Date(bid.created_at).toLocaleString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                      }
                                    )}
                                  </Typography>
                                </Fade>
                              </Slide>
                            </Box>
                          ) : (
                            <Typography key={bid.id} variant="body2">
                              {bid.amount === highestBidAmount ? "👑 " : "👤 "}€
                              {Number(bid.amount).toLocaleString()}
                              {" — "}
                              {bid?.business_name
                                ? bid.business_name + " — "
                                : bid?.first_name + " — "}
                              {new Date(bid.created_at).toLocaleString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                }
                              )}
                            </Typography>
                          );
                        })
                      ) : (
                        <Typography variant="body2">
                          {t("view_auction_page.first_to_bid")}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* SELLER */}
                <Grid item xs={12} md={12}>
                  <Typography
                    sx={{
                      display: "block",
                      mt: { xs: 0, sm: 1 },
                      mb: 1,
                      fontSize: "1.8em",
                      fontWeight: "900",
                    }}
                    variant="body"
                    color="primary"
                  >
                    {t("view_auction_page.verified_seller")}
                  </Typography>
                  <Paper
                    sx={{
                      mb: 3,
                      padding: 0,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: theme.palette.primary.superlight,
                      // borderColor: theme.palette.primary.superlight2
                      borderColor: "transparent",
                    }}
                  >
                    <Grid container>
                      <IconButton
                        disableRipple
                        disableFocusRipple
                        sx={{
                          pointerEvents: "none",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <Badge
                          overlap="circular"
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                          badgeContent={
                            <VerifiedIcon
                              color="secondary"
                              sx={{
                                width: 30,
                                height: 30,
                                backgroundColor: "white",
                                borderRadius: "50%",
                              }}
                            />
                          }
                        >
                          <AccountIcon color="primary" sx={{ fontSize: 110 }} />
                        </Badge>
                      </IconButton>
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        sx={{ minHeight: "100%" }}
                      >
                        <Typography
                          sx={{
                            fontSize: "1.2em",
                            lineHeight: "1em",
                            fontWeight: "bold",
                          }}
                          variant="body"
                          color="primary"
                        >
                          {auction?.business_name ? (
                            auction.business_name
                          ) : (
                            <>
                              {auction?.first_name} {auction?.last_name}
                            </>
                          )}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1em",
                            mt: 0,
                            pt: 0,
                            lineHeight: "1em",
                          }}
                          variant="subtitle2"
                          color="primary"
                        >
                          {auction?.business_name ? (
                            <>
                              {auction?.first_name} {auction?.last_name}
                            </>
                          ) : (
                            t("view_auction_page.private")
                          )}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "1em",
                            mt: 0.6,
                            pt: 0,
                            lineHeight: "1em",
                          }}
                          variant="subtitle2"
                          color="secondary"
                        >
                          {auction?.property_email
                            ? auction.property_email
                            : auction?.email}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1em",
                            mt: 0,
                            pt: 0,
                            lineHeight: "1em",
                          }}
                          variant="subtitle2"
                          color="secondary"
                        >
                          {auction?.property_phone
                            ? auction.property_phone
                            : auction?.phone}
                        </Typography>
                      </Box>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ViewAuction;
